import type { BadgeProps } from '@meterup/atto';
import type { Row } from '@tanstack/table-core';
import { Badge, HStack, styled, Text } from '@meterup/atto';

import type { DataColumnProps } from '../createColumnBuilder';
import { NoValue } from '../../NoValue';

interface DeviceTag {
  UUID: string;
  name: string;
}

interface DeviceWithTags {
  tags?: DeviceTag[];
}

interface VirtualDeviceTagBadgeProps extends BadgeProps {
  tag: DeviceTag;
}

const VirtualDeviceTagBadgeContainer = styled('div', {
  maxWidth: '$120',
});
VirtualDeviceTagBadgeContainer.displayName = 'VirtualDeviceTagBadgeContainer';

const TagsOverflowContainer = styled('div', {
  maxWidth: '$300',
  minWidth: 0,
  overflow: 'auto',
});
TagsOverflowContainer.displayName = 'TagsOverflowContainer';

export function VirtualDeviceTagBadge({ tag, ...props }: VirtualDeviceTagBadgeProps) {
  return (
    <VirtualDeviceTagBadgeContainer>
      <Badge ends="pill" size="small" variant="brand" elide {...props}>
        {tag.name}
      </Badge>
    </VirtualDeviceTagBadgeContainer>
  );
}

export function TableCellVirtualDeviceTags({ row }: { row: DeviceWithTags }) {
  if (!row.tags?.length) {
    return <NoValue />;
  }

  return (
    <TagsOverflowContainer>
      <HStack spacing={4}>
        {row.tags.map((tag) => (
          <VirtualDeviceTagBadge key={tag.UUID} tag={tag} />
        ))}
      </HStack>
    </TagsOverflowContainer>
  );
}

const NONE_OPTION_VALUE = '(None)';

export const tableCellVirtualDeviceTagsProps = {
  header: 'Tags',
  meta: {
    tooltip: {
      icon: {
        icon: 'tag',
        contents: 'Tags',
      },
    },
  },
  cell: TableCellVirtualDeviceTags,
  filterVariant: 'multi-select',
  filterFn: (row: Pick<Row<DeviceWithTags>, 'original'>, _, filterValue: string[] | undefined) =>
    !filterValue?.length ||
    (filterValue.includes(NONE_OPTION_VALUE) && !row.original.tags?.length) ||
    !!row.original.tags?.some((tag) => filterValue.includes(tag.name)),
  filterOptions: (data) => [
    {
      value: NONE_OPTION_VALUE,
      label: <Text italicize="italic">{NONE_OPTION_VALUE}</Text>,
    },
    ...(data?.flatMap(
      (row) =>
        row.tags?.map((tag) => ({
          value: tag.name,
          label: tag.name,
        })) ?? [],
    ) ?? []),
  ],
} satisfies Omit<DataColumnProps<DeviceWithTags, string, string>, 'id'>;
